// Decides when a settled map viewport that rendered no HiiKER trails is worth
// reporting to the backend as a `map_nohits` event, feeding
// SearchCurator::MapGapReport's weekly "where are we missing coverage" report.
//
// All the policy lives here rather than in the map module so it can be unit
// tested without a Mapbox instance, and so the iOS/Android ports have one
// readable reference for the rules.
//
// The zoom band and the 1-degree grid MUST stay in lock-step with
// MapNohitEvent::MIN/MAX_REPORTABLE_ZOOM and MapGapReport::GRID_DEGREES —
// the client uses the grid to dedupe, the server uses it to cluster, and if
// they disagree the report double-counts.

export const MIN_REPORTABLE_ZOOM = 8;
export const MAX_REPORTABLE_ZOOM = 16;
export const GRID_DEGREES = 1;

// A user panning a genuinely empty region crosses cells continuously. One
// event per cell per page visit is enough to establish "somebody looked here
// and found nothing"; everything after that is noise.
export const MAX_EVENTS_PER_SESSION = 20;

// Floor of a pan gesture — moveend fires repeatedly during inertial scroll.
export const MIN_INTERVAL_MS = 5_000;

export interface ViewportSample {
  lon: number;
  lat: number;
  zoom: number;
  /** How many HiiKER trail features the map actually rendered. */
  trailFeatureCount: number;
  /** [west, south, east, north] — optional, stored for later analysis only. */
  bounds?: [number, number, number, number];
}

export interface CoveragePayload {
  latitude: number;
  longitude: number;
  zoom: number;
  platform: 'web';
  boundingBox?: [number, number, number, number];
}

/**
 * Which 1-degree cell a coordinate falls in.
 *
 * Uses floor (not round) so cells tile the plane without gaps or overlaps and
 * negatives bucket consistently — this mirrors MapGapReport.grid_key exactly.
 */
export function gridCellKey(lon: number, lat: number): string {
  return `${Math.floor(lon / GRID_DEGREES)}:${Math.floor(lat / GRID_DEGREES)}`;
}

/**
 * Below the band the viewport spans a country or more, where "no trails on
 * screen" is expected. Above it the user is inspecting one spot rather than
 * looking for something to walk.
 */
export function isReportableZoom(zoom: number): boolean {
  return Number.isFinite(zoom) && zoom >= MIN_REPORTABLE_ZOOM && zoom <= MAX_REPORTABLE_ZOOM;
}

export function hasUsableCoordinates(lon: number, lat: number): boolean {
  return (
    Number.isFinite(lon) &&
    Number.isFinite(lat) &&
    lat >= -90 &&
    lat <= 90 &&
    lon >= -180 &&
    lon <= 180
  );
}

export function buildPayload(sample: ViewportSample): CoveragePayload {
  const payload: CoveragePayload = {
    latitude: sample.lat,
    longitude: sample.lon,
    zoom: sample.zoom,
    platform: 'web',
  };
  if (sample.bounds && sample.bounds.length === 4 && sample.bounds.every(Number.isFinite)) {
    payload.boundingBox = sample.bounds;
  }
  return payload;
}

export interface CoverageReporterOptions {
  send: (payload: CoveragePayload) => void;
  now?: () => number;
  maxEventsPerSession?: number;
  minIntervalMs?: number;
}

export interface CoverageReporter {
  /** Returns true when the sample was actually reported. */
  report: (sample: ViewportSample) => boolean;
}

/**
 * Stateful gate around `send`. Kept as a factory rather than module-level state
 * so tests get a fresh reporter per case and so a second map on the same page
 * doesn't share a budget with the first.
 */
export function createCoverageReporter({
  send,
  now = () => Date.now(),
  maxEventsPerSession = MAX_EVENTS_PER_SESSION,
  minIntervalMs = MIN_INTERVAL_MS,
}: CoverageReporterOptions): CoverageReporter {
  const reportedCells = new Set<string>();
  let sentCount = 0;
  let lastSentAt: number | null = null;

  return {
    report(sample: ViewportSample): boolean {
      if (sample.trailFeatureCount > 0) return false;
      if (!hasUsableCoordinates(sample.lon, sample.lat)) return false;
      if (!isReportableZoom(sample.zoom)) return false;

      const cell = gridCellKey(sample.lon, sample.lat);
      if (reportedCells.has(cell)) return false;
      if (sentCount >= maxEventsPerSession) return false;

      const timestamp = now();
      if (lastSentAt !== null && timestamp - lastSentAt < minIntervalMs) return false;

      reportedCells.add(cell);
      sentCount += 1;
      lastSentAt = timestamp;
      send(buildPayload(sample));
      return true;
    },
  };
}
